/**
 * Breeth buy controller (ticket #6727, October 2026).
 *
 * The one piece of custom code that owns size selection on the product page, the sticky
 * bar and the quick-add popup. Fabric's native variant picker still fetches prices, images,
 * stock and the variant id; this file adds three rules on top:
 *
 *   1. Size starts blank. Add to Cart stays grey and blocked until the shopper picks a size.
 *   2. Colour loads as Black when Black is in stock, otherwise Fabric's first colour with stock.
 *   3. When Add to Cart fires, the variant id sent to the cart matches the colour and size
 *      the shopper can see selected.
 *
 * The same blank-until-picked rule covers the upsell row in the EA slide-out cart (section 8).
 *
 * Shopify Bundles sets (e.g. The Holda Set) have one size option per item, named like
 * "Holda Cotton Leggings - Regular Length (Size)". Every size option starts blank and each
 * one has to be picked before Add to Cart unlocks.
 *
 * State lives on the scope element (the product section, or the quick-add dialog):
 * data-bz-sizes holds the size options picked so far, and data-bz-size="<product id>" is set
 * once every size option is picked. Fabric never morphs those elements, so its re-renders
 * can't wipe the state. Every page load and every popup open starts with no size chosen.
 */
import { StandardEvents } from '@shopify/events';
import { getScrollContainer } from '@theme/scroll-container';

// "Size", or a bundle item's size option such as "Short Sleeve Cotton Crop Top (Size)".
const SIZE_OPTION = /size/i;
const COLOUR_OPTION = /^colou?r$/i;
const PREFERRED_COLOUR = /^(?:\d+\s*x\s*)?black$/i;
const ADD_TO_CART_LABEL = 'Add to cart';

/** @param {Element} el */
const scopeOf = (el) => el.closest('dialog, .shopify-section');

/** @param {Element} scope */
const pickersIn = (scope) =>
  [...scope.querySelectorAll('variant-picker')].filter((picker) => !picker.closest('product-card'));

/** @param {Element} picker @param {RegExp} name */
const inputsFor = (picker, name) =>
  [...picker.querySelectorAll('fieldset input[type="radio"]')].filter((input) =>
    name.test(input.dataset.optionName || '')
  );

/** Size options with a real choice (more than one value), in page order. @param {Element} picker */
const sizeNames = (picker) => {
  const counts = new Map();
  for (const input of inputsFor(picker, SIZE_OPTION)) {
    const name = input.dataset.optionName || '';
    counts.set(name, (counts.get(name) || 0) + 1);
  }
  return [...counts].filter(([, count]) => count > 1).map(([name]) => name);
};

/** @param {Element} picker */
const needsSize = (picker) => sizeNames(picker).length > 0;

/** @param {Element} scope */
const sizePicker = (scope) => pickersIn(scope).find(needsSize) || null;

// Products with a Rapi bundle box (e.g. the Woodridge school bundle). Rapi hides the theme's
// picker and adds whatever its own rows show, one colour and size per item. The size gate stays
// off there: with the picker hidden nobody could pick a size, so Add to Cart would never unlock.
const RAPI_BUNDLE = '.rapi-bundles__bars';

/** @param {Element} scope */
const hasBundleBox = (scope) => !!scope.querySelector(RAPI_BUNDLE);

/**
 * Size options the shopper has picked on this product.
 * @param {HTMLElement} scope @param {Element} picker
 * @returns {string[]}
 */
const pickedNames = (scope, picker) => {
  try {
    const state = JSON.parse(scope.dataset.bzSizes || '{}');
    return state.product === picker.dataset.productId && Array.isArray(state.names) ? state.names : [];
  } catch {
    return [];
  }
};

/** Size options still to pick. @param {HTMLElement} scope @param {Element} picker */
const missingNames = (scope, picker) => {
  const picked = pickedNames(scope, picker);
  return sizeNames(picker).filter((name) => !picked.includes(name));
};

/** @param {Element} scope */
const sizeChosen = (scope) => {
  const picker = sizePicker(scope);
  if (!picker || hasBundleBox(scope)) return true;
  return (
    scope instanceof HTMLElement &&
    scope.dataset.bzSize === picker.dataset.productId &&
    missingNames(scope, picker).length === 0
  );
};

/** @param {Element} scope */
const buyButtons = (scope) =>
  [...scope.querySelectorAll('product-form-component [ref="addToCartButton"], .sticky-add-to-cart__button')].filter(
    (button) => !button.closest('product-card')
  );

/** Sets a button's visible label without touching its icon and checkmark spans. */
const setLabel = (button, text) => {
  const nested = button.querySelector('.add-to-cart-text__content > span');
  if (nested) {
    nested.textContent = text;
    return;
  }
  const node = [...button.childNodes].find((n) => n.nodeType === Node.TEXT_NODE && n.textContent.trim());
  if (node) node.textContent = text;
};

/**
 * Brings the DOM in line with the scope's state. Safe to call any number of times.
 * @param {Element} scope
 */
function enforce(scope) {
  if (!(scope instanceof HTMLElement)) return;
  const picker = sizePicker(scope);
  if (!picker || hasBundleBox(scope)) {
    scope.removeAttribute('data-bz-pending');
    for (const button of buyButtons(scope)) button.removeAttribute('aria-disabled');
    return;
  }

  const chosen = sizeChosen(scope);
  scope.toggleAttribute('data-bz-pending', !chosen);

  if (!chosen) {
    // Fabric re-checks a size whenever it re-renders after a colour change. Until the
    // shopper picks it, that size option shows nothing selected. Options already picked stay.
    const missing = missingNames(scope, picker);
    for (const input of inputsFor(picker, SIZE_OPTION)) {
      if (!missing.includes(input.dataset.optionName || '')) continue;
      if (input.checked) input.checked = false;
      input.dataset.currentChecked = 'false';
      input.dataset.previousChecked = 'false';
    }
  }

  for (const button of buyButtons(scope)) {
    if (chosen) {
      button.removeAttribute('aria-disabled');
    } else {
      button.setAttribute('aria-disabled', 'true');
      setLabel(button, ADD_TO_CART_LABEL);
      // With no size picked, the sticky bar reflects whatever size Fabric resolved behind the
      // scenes, which can be sold out. Keep it clickable so it can point the shopper to the sizes.
      if (button.matches('.sticky-add-to-cart__button')) button.disabled = false;
    }
  }
}

/**
 * Selects Black (or "3 x Black" on bundles) once per load, if it's in stock and the
 * shopper hasn't chosen a colour. Goes through Fabric's own picker so price, images and
 * stock update the normal way.
 * @param {Element} scope
 */
function applyDefaultColour(scope) {
  if (!(scope instanceof HTMLElement) || scope.dataset.bzColourSet) return;
  const picker = pickersIn(scope).find((p) => inputsFor(p, COLOUR_OPTION).length > 1);
  scope.dataset.bzColourSet = 'true';
  if (!picker) return;

  // A colour in the URL (?variant=…) came from a link the shopper followed. Keep it.
  if (scope.matches('.shopify-section') && new URL(location.href).searchParams.has('variant')) return;

  const colours = inputsFor(picker, COLOUR_OPTION);
  const preferred = colours.find(
    (input) => PREFERRED_COLOUR.test(input.value) && input.dataset.optionAvailable !== 'false'
  );
  if (preferred && !preferred.checked) preferred.click();
}

/**
 * Highlights the first size row still to pick when someone tries to add without a size.
 * @param {Element} scope
 */
function nudge(scope) {
  const picker = sizePicker(scope);
  if (!picker || !(scope instanceof HTMLElement)) return;
  const [name] = missingNames(scope, picker);
  const fieldset = inputsFor(picker, SIZE_OPTION)
    .find((input) => !name || input.dataset.optionName === name)
    ?.closest('fieldset');
  if (!fieldset) return;
  const box = fieldset.getBoundingClientRect();
  if (box.top < 0 || box.bottom > window.innerHeight) {
    // Fabric scrolls .page-wrapper on desktop and the document on mobile. On desktop the
    // details column is also sticky, so aim for the top of the section, not the fieldset.
    let sticky = false;
    for (let el = fieldset.parentElement; el && el !== scope; el = el.parentElement) {
      if (getComputedStyle(el).position === 'sticky') sticky = true;
    }
    const container = scope.matches('dialog') ? null : getScrollContainer();
    const behavior = document.hidden || matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth';
    if (container && sticky) {
      const delta = scope.getBoundingClientRect().top - 80;
      container.scrollTo({ top: Math.max(0, container.scrollTop + delta), behavior });
    } else if (container) {
      const delta = box.top + box.height / 2 - window.innerHeight / 2;
      container.scrollTo({ top: Math.max(0, container.scrollTop + delta), behavior });
    } else {
      fieldset.scrollIntoView({ behavior, block: 'center' });
    }
  }
  fieldset.classList.remove('bz-attention');
  void fieldset.offsetWidth; // restart the animation
  fieldset.classList.add('bz-attention');
  setTimeout(() => fieldset.classList.remove('bz-attention'), 1600);
}

/** Variant fetches in flight per scope, so the submit check never fights Fabric mid-update. */
const pending = new WeakMap();

// 1. A real click on a size pill marks that size option as chosen for that product. Once
// every size option is chosen, the scope gets data-bz-size and Add to Cart unlocks.
document.addEventListener(
  'click',
  (event) => {
    if (!event.isTrusted || !(event.target instanceof HTMLInputElement)) return;
    const input = event.target;
    if (input.type !== 'radio' || !SIZE_OPTION.test(input.dataset.optionName || '')) return;
    const picker = input.closest('variant-picker');
    const scope = picker && scopeOf(picker);
    if (!(scope instanceof HTMLElement) || !(picker instanceof HTMLElement)) return;
    const product = picker.dataset.productId || '';
    const names = pickedNames(scope, picker);
    const name = input.dataset.optionName || '';
    if (!names.includes(name)) names.push(name);
    scope.dataset.bzSizes = JSON.stringify({ product, names });
    if (missingNames(scope, picker).length === 0) scope.dataset.bzSize = product;
    else delete scope.dataset.bzSize;
    enforce(scope);
  },
  true
);

// 2. Block Add to Cart (main button, sticky bar, popup) until a size is chosen. Listening on
// window in the capture phase runs this before Fabric's own click handlers.
window.addEventListener(
  'click',
  (event) => {
    const button =
      event.target instanceof Element &&
      event.target.closest('[ref="addToCartButton"], .sticky-add-to-cart__button');
    if (!button || button.closest('product-card')) return;
    const scope = scopeOf(button);
    if (!scope || sizeChosen(scope)) return;
    // Fabric's sticky bar clicks the main button for the shopper. Let only real clicks nudge.
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.isTrusted) nudge(scope);
  },
  true
);

// 3. Last check before the cart request: the id matches the visible colour and size.
window.addEventListener(
  'submit',
  (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || form.dataset.type !== 'add-to-cart-form') return;
    if (form.closest('product-card')) return;
    const scope = scopeOf(form);
    if (!scope) return;

    if (!sizeChosen(scope)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      nudge(scope);
      return;
    }

    // While Fabric is still fetching the new variant, its own queue holds the add and
    // resolves it once the fetch lands. Don't second-guess it.
    if ((pending.get(scope) || 0) > 0) return;

    // Each checked input carries the variant id for its value with the other options as they
    // stand. When they all agree, that is the variant the shopper can see.
    const picker = sizePicker(scope);
    const checked = picker ? [...picker.querySelectorAll('fieldset input[type="radio"]:checked')] : [];
    const ids = new Set(checked.map((input) => input.dataset.variantId).filter(Boolean));
    const idInput = form.querySelector('input[name="id"]');
    // A single-size product keeps the earlier rule: the checked size's id wins.
    const sizes = picker ? inputsFor(picker, SIZE_OPTION).filter((input) => input.checked) : [];
    const expected = ids.size === 1 ? [...ids][0] : sizes.length === 1 ? sizes[0].dataset.variantId : undefined;
    if (expected && idInput instanceof HTMLInputElement && idInput.value !== expected) {
      idInput.value = expected;
    }
  },
  true
);

// 4. After every Fabric variant update, put the size state back.
document.addEventListener(StandardEvents.productSelect, (event) => {
  const scope = event.target instanceof Element ? scopeOf(event.target) : null;
  if (!scope) return;
  pending.set(scope, (pending.get(scope) || 0) + 1);
  const done = () => {
    pending.set(scope, Math.max(0, (pending.get(scope) || 1) - 1));
    enforce(scope);
  };
  // The extra hop lets Fabric's own listeners on the same promise finish first.
  Promise.resolve(event.promise)
    .then(() => undefined)
    .then(done, done);
});

// 5. Quick-add popup: Fabric swaps new product content in each time it opens.
const modalContent = document.getElementById('quick-add-modal-content');
if (modalContent) {
  new MutationObserver(() => {
    const scope = scopeOf(modalContent);
    if (!(scope instanceof HTMLElement)) return;
    delete scope.dataset.bzSize;
    delete scope.dataset.bzSizes;
    delete scope.dataset.bzColourSet;
    enforce(scope);
    applyDefaultColour(scope);
  }).observe(modalContent, { attributes: true, attributeFilter: ['view-event-payload'] });
}

// 6. Open the EA slide-out cart after every successful add from the theme (Marc, 5 Oct).
// EA only opens for adds it makes itself, so the theme asks it to open once Fabric's add lands.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.action !== 'add' || typeof window.openEAMiniCart !== 'function') return;
  Promise.resolve(event.promise)
    .then((result) => {
      if (result?.detail?.didError) return;
      const drawer = document.querySelector('#ea-sticky-cart-root .eascMiniCartDiv');
      if (drawer && drawer.getBoundingClientRect().left < window.innerWidth - 10) return; // already open
      window.openEAMiniCart();
    })
    .catch(() => {});
});

// 7. Page load.
for (const picker of document.querySelectorAll('variant-picker')) {
  const scope = scopeOf(picker);
  if (!scope || scope === modalContent?.closest('dialog')) continue;
  enforce(scope);
  applyDefaultColour(scope);
}

// 7b. Rapi injects its bundle box after page load. When it appears, release the size gate on
// that product section (see hasBundleBox). Stops watching once found, or after 20 seconds.
if (document.querySelector('variant-picker')) {
  const releaseBundles = () => {
    const boxes = document.querySelectorAll(RAPI_BUNDLE);
    for (const box of boxes) {
      const scope = scopeOf(box);
      if (scope) enforce(scope);
    }
    return boxes.length > 0;
  };
  if (!releaseBundles()) {
    let queued = false;
    const watchBundles = new MutationObserver(() => {
      if (queued) return;
      queued = true;
      queueMicrotask(() => {
        queued = false;
        if (releaseBundles()) watchBundles.disconnect();
      });
    });
    watchBundles.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => watchBundles.disconnect(), 20000);
  }
}

// 8. EA cart upsells (Marc, 6 Oct). EA preselects each upsell's first variant (a bundle's
// 6 (XXS), a sock's XS), and its Add button adds that variant when the shopper picked nothing.
// Each dropdown now starts on EA's own "Colour / Size" placeholder, and its Add button stays
// grey and blocked until the shopper picks an option. Same rule as the product page.
const UPSELL_ROW = '#ea-sticky-cart-root .easc-upsellProd';
const UPSELL_SELECT = 'select[name^="relatedVar"]';
const UPSELL_BUTTON = '.easc-upsellCartButtonDiv button';

// Dropdowns the shopper has picked from. When EA rebuilds a row it makes a new <select>,
// which starts blank again, so a stale pick can never carry over to a different item.
const upsellPicked = new WeakSet();

/** The row's dropdown, or null for a one-variant item with nothing to pick. @param {Element} row */
const upsellSelect = (row) => {
  const select = row.querySelector(UPSELL_SELECT);
  if (!(select instanceof HTMLSelectElement)) return null;
  return [...select.options].filter((option) => option.value !== '').length > 1 ? select : null;
};

/** @param {Element} row */
const upsellReady = (row) => {
  const select = upsellSelect(row);
  return !select || (upsellPicked.has(select) && select.value !== '');
};

function enforceUpsells() {
  for (const row of document.querySelectorAll(UPSELL_ROW)) {
    const select = upsellSelect(row);
    if (select && !upsellPicked.has(select) && select.value !== '') {
      let placeholder = [...select.options].find((option) => option.value === '');
      if (!placeholder) {
        placeholder = new Option('Colour / Size', '');
        placeholder.disabled = true;
        select.prepend(placeholder);
      }
      placeholder.selected = true;
    }
    const button = row.querySelector(UPSELL_BUTTON);
    if (!button) continue;
    if (upsellReady(row)) button.removeAttribute('aria-disabled');
    else button.setAttribute('aria-disabled', 'true');
  }
}

// A pick from the dropdown unlocks that row's Add button.
document.addEventListener(
  'change',
  (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.matches(UPSELL_SELECT)) return;
    if (!select.closest(UPSELL_ROW)) return;
    if (select.value !== '') upsellPicked.add(select);
    enforceUpsells();
  },
  true
);

// Block Add until a pick. Window capture runs before EA's own (React) click handler.
window.addEventListener(
  'click',
  (event) => {
    const button = event.target instanceof Element && event.target.closest(UPSELL_BUTTON);
    const row = button && button.closest(UPSELL_ROW);
    if (!row || upsellReady(row)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const select = upsellSelect(row);
    if (!event.isTrusted || !select) return;
    select.classList.remove('bz-attention');
    void select.offsetWidth; // restart the animation
    select.classList.add('bz-attention');
    setTimeout(() => select.classList.remove('bz-attention'), 1600);
    select.focus({ preventScroll: true });
  },
  true
);

// EA renders the cart into #ea-sticky-cart-root (a direct child of <body>) and re-renders it
// after every cart change. Re-apply the rule after each render, before the browser paints, so
// the default variant never flashes up. (A microtask, not requestAnimationFrame: rAF pauses in
// background tabs.)
const watchUpsells = (root) => {
  let queued = false;
  new MutationObserver(() => {
    if (queued) return;
    queued = true;
    queueMicrotask(() => {
      queued = false;
      enforceUpsells();
    });
  }).observe(root, { childList: true, subtree: true });
  enforceUpsells();
};
const eaRoot = document.getElementById('ea-sticky-cart-root');
if (eaRoot) {
  watchUpsells(eaRoot);
} else {
  const waitForEa = new MutationObserver(() => {
    const root = document.getElementById('ea-sticky-cart-root');
    if (!root) return;
    waitForEa.disconnect();
    watchUpsells(root);
  });
  waitForEa.observe(document.body, { childList: true });
}
